import { defineStore } from "pinia";
import { fetchPostData } from "~/composables/general";
import { useUserStore } from "~/store/auth";
import WWSS from "~/utils/wwss";

const pad = (value) => String(value).padStart(2, "0");

const getBlockingDate = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return formatBlockingDate(date);
};

const formatBlockingDate = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} 00:00:00`;

const getPreviousYearDate = (blockingDate) => {
  const date = new Date(`${blockingDate.replace(" ", "T")}Z`);
  date.setUTCFullYear(date.getUTCFullYear() - 1);
  return formatBlockingDate(date);
};

const getVersions = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.versions)) return response.versions;
  if (Array.isArray(response?.apps)) return response.apps;
  return [];
};

const getErrorMessage = (error) => {
  if (typeof error === "string") return error;
  return error?.message || error?.description || error?.code || "Error desconocido";
};

const formatSuiteDate = (value) =>
  value && value.length === 10 ? `${value} 00:00:00` : value;

export const useVersionControl = defineStore("versionControl", {
  state: () => ({
    visible: false,
    loading: false,
    versions: [],
    error: "",
    blockingDate: "",
    deleting: false,
    deletionError: "",
    creating: false,
    creationError: "",
    updating: false,
    updateError: "",
  }),
  actions: {
    async setVisible(value) {
      this.visible = value;
      if (value) await this.fetchVersions({ reset: true });
    },

    async fetchVersions({ reset = false, blockingDate = getBlockingDate() } = {}) {
      this.loading = true;
      this.error = "";

      if (reset) this.blockingDate = blockingDate;

      try {
        const response = await fetchPostData(WWSS.suite.app.list, {
          project: "MXM",
          enabled: 1,
          blockingDate,
        });

        if (response?.error) {
          this.versions = [];
          this.error = getErrorMessage(response.error);
          return;
        }

        const versions = getVersions(response);
        const mergedVersions = reset ? versions : [...this.versions, ...versions];

        this.versions = mergedVersions.filter(
          (version, index, allVersions) =>
            allVersions.findIndex(
              (candidate) =>
                (candidate.versionId && candidate.versionId === version.versionId) ||
                (candidate.versionUid && candidate.versionUid === version.versionUid)
            ) === index
        ).sort((first, second) =>
          String(second?.versionCode ?? "").localeCompare(
            String(first?.versionCode ?? ""),
            undefined,
            { numeric: true }
          )
        );
      } catch (error) {
        this.versions = [];
        this.error = getErrorMessage(error);
      } finally {
        this.loading = false;
      }
    },

    async fetchPreviousVersions() {
      if (this.loading || !this.blockingDate) return;

      const previousDate = getPreviousYearDate(this.blockingDate);
      this.blockingDate = previousDate;
      await this.fetchVersions({ blockingDate: previousDate });
    },

    async deleteVersion(versionUid) {
      this.deleting = true;
      this.deletionError = "";

      try {
        const userStore = useUserStore();
        const token =
          userStore.login?.token ||
          JSON.parse(localStorage.getItem("login") || "{}").token;

        const response = await fetchPostData(WWSS.suite.app.update, {
          versionUid,
          enabled: 0,
        }, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response?.error) {
          this.deletionError = getErrorMessage(response.error);
          return false;
        }

        this.versions = this.versions.filter(
          (version) => version.versionUid !== versionUid
        );
        return true;
      } catch (error) {
        this.deletionError = getErrorMessage(error);
        return false;
      } finally {
        this.deleting = false;
      }
    },

    async createVersion(versionData) {
      this.creating = true;
      this.creationError = "";

      try {
        const userStore = useUserStore();
        const token =
          userStore.login?.token ||
          JSON.parse(localStorage.getItem("login") || "{}").token;
        const userUid = userStore.login?.AliasUsuario;
        const payload = {
          versionCode: versionData.versionCode,
          description: versionData.description,
          userUid,
          project: "MXM",
        };

        if (versionData.notificationDate) {
          payload.notificationDate = formatSuiteDate(versionData.notificationDate);
        }
        if (versionData.blockingDate) {
          payload.blockingDate = formatSuiteDate(versionData.blockingDate);
        }

        const response = await fetchPostData(WWSS.suite.app.create, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response?.error) {
          this.creationError = getErrorMessage(response.error);
          return false;
        }

        const createdVersion = Array.isArray(response)
          ? response[0]
          : response?.data || response;
        const version = {
          ...versionData,
          ...createdVersion,
          userUid: createdVersion?.userUid || userUid,
          versionCode: createdVersion?.versionCode || versionData.versionCode,
        };

        this.versions = [version, ...this.versions].filter(
          (item, index, allVersions) =>
            allVersions.findIndex(
              (candidate) =>
                (candidate.versionId && candidate.versionId === item.versionId) ||
                (candidate.versionUid && candidate.versionUid === item.versionUid) ||
                candidate.versionCode === item.versionCode
            ) === index
        ).sort((first, second) =>
          String(second?.versionCode ?? "").localeCompare(
            String(first?.versionCode ?? ""),
            undefined,
            { numeric: true }
          )
        );
        return true;
      } catch (error) {
        this.creationError = getErrorMessage(error);
        return false;
      } finally {
        this.creating = false;
      }
    },

    async updateVersion(versionData) {
      this.updating = true;
      this.updateError = "";

      try {
        const userStore = useUserStore();
        const token =
          userStore.login?.token ||
          JSON.parse(localStorage.getItem("login") || "{}").token;
        const userUid = userStore.login?.AliasUsuario;
        const payload = {
          versionUid: versionData.versionUid,
          description: versionData.description,
          userUid,
          notificationDate: versionData.notificationDate || null,
          blockingDate: versionData.blockingDate || null,
        };

        const response = await fetchPostData(WWSS.suite.app.update, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response?.error) {
          this.updateError = getErrorMessage(response.error);
          return false;
        }

        const responseVersion = Array.isArray(response)
          ? response[0]
          : response?.data || response;
        const updatedVersion = {
          ...versionData,
          ...responseVersion,
          versionUid: versionData.versionUid,
          versionCode: versionData.versionCode,
          userUid: responseVersion?.userUid || userUid,
          notificationDate: versionData.notificationDate || null,
          blockingDate: versionData.blockingDate || null,
        };
        const versionIndex = this.versions.findIndex(
          (version) => version.versionUid === versionData.versionUid
        );

        if (versionIndex !== -1) this.versions.splice(versionIndex, 1, updatedVersion);
        return true;
      } catch (error) {
        this.updateError = getErrorMessage(error);
        return false;
      } finally {
        this.updating = false;
      }
    },
  },
});